
import { Component, Prop, Vue } from 'vue-property-decorator';
import { DateTime } from 'luxon';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { Action, Getter } from 'vuex-class';
import { formatRepeatingOrdersUnavailableDates, formatRepeatingOrdersUnavailableDatesListHtml } from '@/utils/format';
import { addToCartExceedLimit, addToCartExceedMealsLimit } from '@/utils/helpers';
import { Validate } from '@/types';
import InfoBanner from '@/components/shared/InfoBanner.vue';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import Modal from '@/components/shared/Modal.vue';
import WarningModal from '@/components/shared/WarningModal.vue';
import '@/validation-rules';

const namespace: string = 'restaurant';

@Component<RepeatingOrdersModal>({
	components: {
		InfoBanner,
		LoadingSpinner,
		Modal,
		WarningModal,
		ValidationProvider,
		ValidationObserver
	}
})
export default class RepeatingOrdersModal extends Vue {
	@Prop({ type: Boolean, required: true }) private showModal!: boolean;
	@Prop({ type: Array, required: false, default: () => [] }) private unavailableDates!: string[];
	@Action('fetchRepeatingOrdersMenus', { namespace }) private fetchRepeatingOrdersMenus!: () => Promise<void>;
	@Action('setRepeatingOrdersUnavailableDates', { namespace }) private setRepeatingOrdersUnavailableDates!: (dates: string[]) => void;
	@Action('setRepeatingOrdersSelectedMealType', { namespace }) private setRepeatingOrdersSelectedMealType!: (mealType: string) => void;
	@Action('setTemporaryItem', { namespace: 'cart' }) private setTemporaryItem!: (temporaryItemPayload: { tempItem: MenuItem|null, quantity?: number }) => void;
	@Action('addToCart', { namespace: 'cart' }) private addToCart!: (extraData: any) => Promise<void>;
	@Action('clearCartItems', { namespace: 'cart' }) private clearCartItems!: () => void;
	@Getter('getRepeatingOrdersMenus', { namespace }) private repeatingOrdersMenus!: RotationalMenu[];
	@Getter('getRepeatingOrdersSelectedMealType', { namespace }) private storedSelectedMealType!: string;
	@Getter('getRepeatingOrdersUnavailableDates', { namespace }) private unavailableDatesForMealType!: string[];
	@Getter('getMaxItemsPerCart', { namespace }) private maxItemsPerCart!: number;
	@Getter('getMaxMealsPerCart', { namespace }) private maxMealsPerCart!: number;
	@Getter('orderingRequiresLogin', { namespace }) private orderingRequiresLogin!: boolean;
	@Getter('getItems', { namespace: 'cart' }) private cartItems!: OrderItem[];
	@Getter('getRotationalMenuOrders', { namespace: 'cart' }) private rotationalMenuOrders!: RotationalMenuOrder[];
	@Getter('isSocialLoginUser', { namespace: 'auth' }) private isSocialLoginUser!: boolean;
	@Getter('isAnonymousUser', { namespace: 'auth' }) private isAnonymousUser!: boolean;

	$refs!: { observer: Validate };

	private loading: boolean = false;
	private showConfirmation: boolean = false;
	private selectedMealType: string = '';
	private validatedItemsCache: Set<number> = new Set();
	private showWarningModal: boolean = false;

	/**
	 * Get unique meal type options from repeating orders menus
	 *
	 * @return {string[]}
	 */
	private get mealTypeOptions(): string[] {
		const menuNames = new Set<string>();

		this.repeatingOrdersMenus.forEach((rotationalMenu: RotationalMenu) => {
			rotationalMenu.menus.forEach((menu: Menu) => {
				if (menu.name) {
					menuNames.add(menu.name);
				}
			});
		});

		return Array.from(menuNames);
	}

	/**
	 * Get and format the end date from the last rotational menu for display
	 *
	 * @return {string}
	 */
	private get endDate(): string {
		if (this.repeatingOrdersMenus.length) {
			const date = this.repeatingOrdersMenus[this.repeatingOrdersMenus.length - 1].date;
			return this.formatDate(date);
		}
		return '';
	}

	/**
	 * Format unavailable dates for display (when 3 or fewer)
	 *
	 * @return {string}
	 */
	private get formattedUnavailableDates(): string {
		return formatRepeatingOrdersUnavailableDates(this.unavailableDatesForMealType);
	}

	/**
	 * Generate HTML for unavailable dates list (when more than 3)
	 *
	 * @return {string}
	 */
	private get unavailableDatesListHtml(): string {
		return formatRepeatingOrdersUnavailableDatesListHtml(this.unavailableDatesForMealType, this.selectedMealType);
	}

	/**
	 * Here we fetch the repeating orders menus if not already cached,
	 * and set the selected meal type from the store.
	 *
	 * @return {Promise<void>}
	 */
	private async mounted(): Promise<void> {
		if (!this.repeatingOrdersMenus.length) {
			await this.loadRepeatingOrdersMenus();
		}
		this.selectedMealType = this.storedSelectedMealType;
	}

	/**
	 * Load repeating orders menus (only if not already cached)
	 *
	 * @return {Promise<void>}
	 */
	private async loadRepeatingOrdersMenus(): Promise<void> {
		try {
			this.loading = true;
			await this.fetchRepeatingOrdersMenus();
		}
		catch (error) {
			this.$toasted.error(this.$t('menu.repeating_orders.error_fetching_menus'), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			this.loading = false;
		}
	}

	/**
	 * Format a single date
	 *
	 * @param {string} date
	 * @return {string}
	 */
	private formatDate(date: string): string {
		return DateTime.fromISO(date).setLocale(this.$i18n.locale).toFormat(this.$t('menu.repeating_orders.date_format'));
	}

	/**
	 * Handle meal type change - check for unavailable dates
	 *
	 * @param {string} mealType
	 * @return {void}
	 */
	private onMealTypeChange(mealType: string): void {
		this.setRepeatingOrdersSelectedMealType(mealType);
		this.selectedMealType = mealType;

		if (!mealType) {
			this.setRepeatingOrdersUnavailableDates([]);
			return;
		}

		const unavailableDates: string[] = [];

		// Check each day to see if the selected meal type is available
		this.repeatingOrdersMenus.forEach((rotationalMenu: RotationalMenu) => {
			const hasMealType = rotationalMenu.menus.some((menu: Menu) => menu.name === mealType);

			if (!hasMealType) {
				unavailableDates.push(rotationalMenu.date);
			}
		});

		this.setRepeatingOrdersUnavailableDates(unavailableDates);
	}

	/**
	 * Handle save button click - show confirmation first
	 *
	 * @return {Promise<void>}
	 */
	private async handleSave(): Promise<void> {
		const isValid = await this.$refs.observer.validate();
		if(isValid) {
			this.showConfirmation = true;
		}
	}

	/**
	 * Handle confirmation - clear cart and add repeating orders
	 * Notes:
	 * * Currently only supports menus without sections (direct items on menu)
	 * * Currently skip allergen check
	 * * Currently makes an API call for each item added to cart. Improvement logged: https://app8pay.atlassian.net/browse/API-1086
	 *
	 * @return {void}
	 */
	private async confirmSave(): Promise<void> {
		try {
			if(this.checkIfLoginIsRequired()) {
				this.showConfirmation = false;
				return;
			}

			// K12 social login restriction
			if (this.isSocialLoginUser) {
				this.$toasted.error(this.$t('utils.vuex_store.cart.k12_social_login_restriction'), { type: 'error', position: 'top-center' }).goAway(5000);
				this.showConfirmation = false;
				this.closeModal();
				return;
			}

			this.showConfirmation = false;
			this.loading = true;

			// We want to clear the cart before adding repeating orders to avoid conflicts
			this.clearCartItems();

			for (const rotationalMenu of this.repeatingOrdersMenus) {
				// Skip dates that are unavailable for this meal type
				if (this.unavailableDatesForMealType.includes(rotationalMenu.date)) {
					continue;
				}

				const selectedMenu = rotationalMenu.menus.find((menu: Menu) => menu.name === this.selectedMealType);

				if (selectedMenu?.items?.length) {
					const firstItem = selectedMenu.items[0];
					this.setTemporaryItem({ tempItem: firstItem, quantity: 1 });

					// Check if adding this item would exceed cart limits
					if (addToCartExceedLimit()) {
						this.$toasted.error(this.$tc('menu.item_viewer.error_item_limit_exceeded', this.maxItemsPerCart, { maxItemsPerCart: this.maxItemsPerCart }), { type: 'error', position: 'top-center' }).goAway(5000);
						this.closeModal();
						return;
					}

					// Check if adding this item would exceed meals limit for this specific date
					if (addToCartExceedMealsLimit(rotationalMenu.date)) {
						this.$toasted.error(this.$tc('menu.item_viewer.error_quantity_meals_limit_exceeded_k12', this.maxMealsPerCart, { maxMealsPerCart: this.maxMealsPerCart }), { type: 'error', position: 'top-center' }).goAway(5000);
						this.closeModal();
						return;
					}

					await this.addToCart({
						special_instructions: '',
						originalPrice: firstItem.price,
						type: 'repeatingOrder',
						orderDate: rotationalMenu.date
					});
				}
			}

			this.$toasted.success(this.$t('menu.repeating_orders.success_message'), { type: 'success', position: 'top-center' }).goAway(3000);
			this.closeModal();
		}
		catch (error) {
			this.$toasted.error(this.$t('menu.repeating_orders.error_adding_orders'), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			this.loading = false;
		}
	}

	/**
	 * Display an error when adding an item to the cart, if ordering requires login and the user is anonymous
	 *
	 * @return {boolean}
	 */
	private checkIfLoginIsRequired(): boolean {
		if(this.isAnonymousUser && this.orderingRequiresLogin) {
			this.showWarningModal = true;
			return true;
		}
		return false;
	}

	/**
	 * Redirect user to log in
	 *
	 * @return {void}
	 */
	private redirectToLogin(): void {
		this.$router.push({ path: '/login', query: this.$route.query, hash: this.$route.hash }).catch(() => {});
	}

	/**
	 * Cancel confirmation
	 *
	 * @return {void}
	 */
	private cancelConfirmation(): void {
		this.showConfirmation = false;
	}

	/**
	 * Close the modal
	 *
	 * @return {void}
	 */
	private closeModal(): void {
		this.$emit('close');
	}
}
